import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type OrganizationRole = "admin" | "owner" | "member";

interface CurrentProfile {
  organizationId: string | null;
  role: OrganizationRole;
  isAdmin: boolean;
  userLocale: string | null;
  profileCountry: string | null;
  organizationCountry: string | null;
  organizationCurrency: string | null;
  organizationMinMargin: number | null;
}

const normalizeRole = (role: string | null | undefined): OrganizationRole => {
  if (role === "admin" || role === "owner") return role;
  return "member";
};

export const useCurrentProfile = () => {
  const [profile, setProfile] = useState<CurrentProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    setLoadingProfile(true);
    setProfileError(null);

    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) {
      setProfileError(sessionError.message);
      setLoadingProfile(false);
      return;
    }

    if (!session?.user) {
      setProfile(null);
      setLoadingProfile(false);
      return;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("organization_id, role, country, user_locale")
      .eq("user_id", session.user.id)
      .single();

    if (error) {
      setProfileError(error.message);
      setLoadingProfile(false);
      return;
    }

    const role = normalizeRole(data?.role);
    let organizationCountry: string | null = null;
    let organizationCurrency: string | null = null;
    let organizationMinMargin: number | null = null;

    if (data?.organization_id) {
      const { data: orgData } = await supabase
        .from("organizations")
        .select("country, currency, min_margin")
        .eq("id", data.organization_id)
        .single();

      organizationCountry = orgData?.country ?? null;
      organizationCurrency = orgData?.currency ?? null;
      organizationMinMargin = orgData?.min_margin ?? null;
    }

    setProfile({
      organizationId: data?.organization_id ?? null,
      role,
      isAdmin: role === "admin" || role === "owner",
      userLocale: data?.user_locale ?? null,
      profileCountry: data?.country ?? null,
      organizationCountry,
      organizationCurrency,
      organizationMinMargin,
    });
    setLoadingProfile(false);
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    const handleProfileUpdated = () => {
      void loadProfile();
    };

    window.addEventListener("orzio-profile-updated", handleProfileUpdated);
    return () => window.removeEventListener("orzio-profile-updated", handleProfileUpdated);
  }, [loadProfile]);

  return { profile, loadingProfile, profileError, reloadProfile: loadProfile };
};
